import { Badge } from '@/shared/components/ui/Badge'
import { useTranslation } from '@/shared/i18n/useTranslation'
import { SurvivorLiveResult, SurvivorPickResult } from '../types'
import type { SurvivorPeerPick } from '../types'

type PickResultBadgeProps = {
  result: SurvivorPeerPick['result']
  /** Provisional outcome while the round is still being played. */
  liveResult?: SurvivorPeerPick['liveResult']
}

/**
 * One badge for a pick's outcome, shared by the history and the peer list.
 *
 * They used to word the same states differently — the history said "Vitória"
 * where the peer list said "Venceu" — so a player saw two vocabularies for one
 * concept on the same screen. One component, one set of keys.
 */
export function PickResultBadge({ result, liveResult }: PickResultBadgeProps) {
  const { t } = useTranslation()

  // While Finalize has not resolved the pick, the picked team's live match
  // outcome is more useful than a blanket "pending" — flagged as provisional.
  if (result === SurvivorPickResult.Pending && liveResult) {
    if (liveResult === SurvivorLiveResult.Live) {
      return <Badge tone="live" variant="soft" hasDot>{t('fixture.live')}</Badge>
    }

    const won = liveResult === SurvivorLiveResult.Won
    return (
      <Badge tone={won ? 'alive' : 'out'} variant="outline">
        {won ? t('survivor.result.through') : t('survivor.status.eliminated')}
        {' · '}
        {t('survivor.result.provisional')}
      </Badge>
    )
  }

  if (result === SurvivorPickResult.Won) {
    return <Badge tone="alive" variant="soft">{t('survivor.pick.won')}</Badge>
  }
  if (result === SurvivorPickResult.Lost) {
    return <Badge tone="out" variant="soft">{t('survivor.pick.lost')}</Badge>
  }
  if (result === SurvivorPickResult.Forfeit) {
    return <Badge tone="out" variant="outline">{t('survivor.result.forfeit')}</Badge>
  }

  return <Badge tone="neutral" variant="soft">{t('survivor.pick.pending')}</Badge>
}
